/**
 * ReorderList Component
 * A horizontal drag & drop list for reordering actions
 * Shows a placeholder indicator where the item will be dropped
 */

import { useState, useRef, useCallback, useEffect } from 'react';

interface ReorderItem {
  codigoUnico: string;
  label: string;
  orden: number;
  tipo: string;
  tieneIA?: boolean;
  isGroup?: boolean;
  childCount?: number;
}

interface ReorderListProps {
  items: ReorderItem[];
  onReorder: (newOrder: string[]) => void;
  onExit: () => void;
}

export default function ReorderList({ items, onReorder, onExit }: ReorderListProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter out inicio and fin
  const reorderableItems = items.filter(
    item => item.tipo !== 'inicio' && item.tipo !== 'fin'
  );

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));

    // Create custom drag image
    const target = e.currentTarget as HTMLElement;
    const clone = target.cloneNode(true) as HTMLElement;
    clone.style.transform = 'rotate(2deg) scale(1.05)';
    clone.style.opacity = '0.9';
    clone.style.position = 'absolute';
    clone.style.top = '-1000px';
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, 50, 20);
    setTimeout(() => clone.remove(), 0);
  }, []);

  // Handle drag over
  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midpoint = rect.left + rect.width / 2;

    // Determine if drop should be before or after this item
    const newDropIndex = e.clientX < midpoint ? index : index + 1;

    if (newDropIndex !== dropIndex) {
      setDropIndex(newDropIndex);
    }
  }, [dropIndex]);

  // Handle drag leave
  const handleDragLeave = useCallback(() => {
    // Don't clear immediately - let dragover on other items update it
  }, []);

  // Handle drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    if (draggedIndex === null || dropIndex === null) return;

    // Calculate actual new position
    let targetIndex = dropIndex;
    if (targetIndex > draggedIndex) {
      targetIndex--;
    }

    if (targetIndex !== draggedIndex) {
      // Create new order
      const newItems = [...reorderableItems];
      const [removed] = newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, removed);

      // Emit new order
      const newOrder = newItems.map(item => item.codigoUnico);
      onReorder(newOrder);
    }

    setDraggedIndex(null);
    setDropIndex(null);
  }, [draggedIndex, dropIndex, reorderableItems, onReorder]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDropIndex(null);
  }, []);

  // Handle container drag over (for dropping at the end)
  const handleContainerDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Get accent color based on type
  const getAccentColor = (item: ReorderItem) => {
    if (item.tieneIA) return '#10b981';
    if (item.tipo === 'grupo') return '#f59e0b';
    if (item.tipo === 'checkpoint') return '#6366f1';
    if (item.tipo === 'posta') return '#8b5cf6';
    if (item.tipo === 'alternativa') return '#ec4899';
    if (item.tipo === 'seccion') return '#06b6d4';
    return '#94a3b8';
  };

  // Keyboard handler for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onExit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onExit]);

  return (
    <div className="reorder-list-container">
      {/* Header */}
      <div className="reorder-header">
        <div className="reorder-header-left">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
          <span>Modo Reordenar</span>
          <span className="reorder-hint">Arrastra los elementos para cambiar el orden</span>
        </div>
        <button onClick={onExit} className="reorder-exit-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
          Salir
        </button>
      </div>

      {/* Items list */}
      <div
        ref={containerRef}
        className="reorder-items"
        onDragOver={handleContainerDragOver}
        onDrop={handleDrop}
      >
        {reorderableItems.map((item, index) => (
          <div key={item.codigoUnico} className="reorder-item-wrapper">
            {/* Drop indicator before item */}
            {dropIndex === index && draggedIndex !== null && draggedIndex !== index && (
              <div className="reorder-drop-indicator" />
            )}

            {/* Item */}
            <div
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDragEnd={handleDragEnd}
              className={`reorder-item ${draggedIndex === index ? 'dragging' : ''}`}
              style={{
                borderLeftColor: getAccentColor(item),
              }}
            >
              {/* Order badge */}
              <span
                className="reorder-item-order"
                style={{ background: getAccentColor(item) }}
              >
                {item.orden}
              </span>

              {/* Label */}
              <span className="reorder-item-label">
                {item.label}
              </span>

              {/* Type indicator */}
              {item.isGroup && (
                <span className="reorder-item-badge group">
                  Grupo {item.childCount ? `(${item.childCount})` : ''}
                </span>
              )}
              {item.tieneIA && (
                <span className="reorder-item-badge ia">IA</span>
              )}

              {/* Drag handle */}
              <svg className="reorder-handle" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="5" r="1" fill="currentColor" />
                <circle cx="9" cy="12" r="1" fill="currentColor" />
                <circle cx="9" cy="19" r="1" fill="currentColor" />
                <circle cx="15" cy="5" r="1" fill="currentColor" />
                <circle cx="15" cy="12" r="1" fill="currentColor" />
                <circle cx="15" cy="19" r="1" fill="currentColor" />
              </svg>
            </div>

            {/* Drop indicator after last item */}
            {index === reorderableItems.length - 1 &&
             dropIndex === reorderableItems.length &&
             draggedIndex !== null &&
             draggedIndex !== reorderableItems.length - 1 && (
              <div className="reorder-drop-indicator" />
            )}
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div className="reorder-footer">
        <span>💡 Los cambios se guardan automáticamente</span>
        <span>Presiona <kbd>Esc</kbd> para salir</span>
      </div>
    </div>
  );
}
